/**
 * What the state of the sharing connection amounts to, in one word and one colour.
 *
 * Sharing has two prerequisites and they fail differently. The first is the server, which
 * introduces this session to the account's others; without it there is nobody to share
 * with and nothing to be done but wait. The second is a network route between this browser
 * and each of those others, which the server has no part in and cannot promise — two
 * sessions can both be signed in, both be visible to each other, and still be unable to
 * exchange a byte. That second failure is invisible until somebody presses the button,
 * which is the whole reason for showing any of this.
 *
 * ## Asleep is not broken
 *
 * A session whose computer has suspended, or whose tab the browser has frozen, stops
 * answering without anything being wrong: nobody is at it. Its socket to the server may
 * stay open for a long time afterwards, so it goes on being *present* while being quite
 * unable to take part in a share.
 *
 * Calling that a fault would light the indicator amber every night, on every account with
 * a headset left on a shelf, and an indicator that is amber every night is one nobody
 * reads. So a session that has stopped answering at all is [`dormant`](LinkState) and
 * counts towards neither the good nor the bad — it is reported, in words, and left alone.
 * The distinction is worth drawing because the two look identical from the outside and
 * mean opposite things: a dormant session will take part as soon as somebody goes back to
 * it, and an unreachable one will not, however long you wait.
 *
 * ## Why the count and not just the colour
 *
 * Amber says something is wrong with one of them. Which one, and how many are fine, is the
 * difference between "share anyway, it will reach the laptop" and "this will not work" —
 * so the summary carries the numbers and the colour is only the headline.
 */

/** What is known about this session's ability to reach one other session. */
export type LinkState =
  /** A connection to it was opened, recently enough to still believe in. */
  | 'reachable'
  /** It is there and answering, but no route to it could be found. */
  | 'unreachable'
  /** Present, but not answering: suspended, or frozen in the background. */
  | 'dormant'
  /** Not established yet — newly arrived, or the check is still running. */
  | 'unknown';

export type HealthLevel =
  /** Everything that could take part in a share can be reached. */
  | 'good'
  /** Some can and some cannot. */
  | 'partial'
  /** Nothing can: either the server is out of reach, or every session is. */
  | 'bad'
  /** Nothing to report — nobody else is there, or it is too early to say. */
  | 'idle';

export interface Health {
  readonly level: HealthLevel;
  /** One line, short enough for the sidebar. */
  readonly summary: string;
  /** The rest of it, for a tooltip: what the line means and what to do about it. */
  readonly detail: string;
}

const NO_SERVER =
  'This browser cannot reach the service that introduces your sessions to one ' +
  'another, so there is nobody to share with until it can. It keeps trying, and this ' +
  'will right itself on its own if the connection comes back.';

const NOBODY =
  'Connected, with no other session of this account signed in. Open this site in ' +
  'another browser, or on your headset, and sign in there too.';

const CHECKING = 'Working out which of your other sessions can be reached from here.';

const ALL_WELL =
  'Every other session of this account answered, and a direct connection to each of ' +
  'them was opened. Sharing will copy models to all of them.';

const NO_ROUTE =
  'Those sessions are signed in and answering, but no direct connection to them could ' +
  'be opened. Models are sent straight from one browser to the other rather than ' +
  'through the server, so this needs a network route between the two — which usually ' +
  'means being on the same home or office network. Between a home network and a mobile ' +
  'connection it often does not work.';

const ASLEEP =
  'A session counts as asleep when it has stopped answering altogether — its computer ' +
  'suspended, or its tab frozen by the browser for being left in the background. That ' +
  'is not a fault, and nothing needs doing about it: it will answer again as soon as ' +
  'somebody goes back to it. It cannot take part in a share until then.';

/**
 * Boil the per-session facts down to something worth putting on a lamp.
 *
 * `connected` is the socket to the server; `links` is one entry per other session it has
 * told us about. Deliberately takes no clock and holds no state, so that every rule below
 * is one the tests can state outright.
 */
export function describeHealth(connected: boolean, links: readonly LinkState[]): Health {
  if (!connected) {
    return { level: 'bad', summary: 'Sharing service unreachable', detail: NO_SERVER };
  }

  if (links.length === 0) {
    return { level: 'idle', summary: 'No other sessions open', detail: NOBODY };
  }

  const reachable = tally(links, 'reachable');
  const unreachable = tally(links, 'unreachable');
  const dormant = tally(links, 'dormant');
  const checking = tally(links, 'unknown');

  // Nothing is known to be wrong and nothing is known to be right: everything present is
  // either asleep or still being looked at. Neither is a fault, so neither is a colour.
  if (reachable === 0 && unreachable === 0) {
    return checking > 0
      ? { level: 'idle', summary: `Checking ${sessions(checking)}…`, detail: CHECKING }
      : { level: 'idle', summary: `${sessions(dormant)} asleep`, detail: ASLEEP };
  }

  const rest = dormant === 0 ? '' : `, ${dormant.toString()} asleep`;
  const detail = (first: string): string =>
    dormant === 0 ? first : `${first}\n\n${ASLEEP}`;

  if (unreachable === 0) {
    return {
      level: 'good',
      summary: `Connected to ${sessions(reachable)}${rest}`,
      detail: detail(ALL_WELL),
    };
  }

  if (reachable === 0) {
    return {
      level: 'bad',
      summary: `Cannot reach ${sessions(unreachable)}${rest}`,
      detail: detail(NO_ROUTE),
    };
  }

  return {
    level: 'partial',
    summary:
      `${unreachable.toString()} of ${sessions(reachable + unreachable)} ` +
      `unreachable${rest}`,
    detail: detail(NO_ROUTE),
  };
}

function tally(links: readonly LinkState[], state: LinkState): number {
  return links.filter((link) => link === state).length;
}

function sessions(count: number): string {
  return count === 1 ? '1 session' : `${count.toString()} sessions`;
}
